import { useInView } from 'react-intersection-observer'

import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'

import { indexes } from '@mab-common/utils'

import classnames from 'classnames'

import AnimatedSectionTitle from '~/components/atoms/AnimatedSectionTitle/AnimatedSectionTitle'
import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import type { ProfileImageProps } from '~/components/atoms/ProfileImage/ProfileImage'
import ProfileImage, {
  ProfileImageType,
} from '~/components/atoms/ProfileImage/ProfileImage'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import type { ComponentTheme } from '~/components/themes'
import type { TSearchAnalystEventProps } from '~/services/elastic/analyst'
import { useMediaQuery } from '~/services/useMediaQuery'
import uniqueObjects from '~/utils/uniqueObjects'
import RecordTypes = indexes.RecordTypes
import { multiPostHogEventPusher } from '~/utils/analyticsPusher'

import containerStyles from '../../atoms/Container/Container.module.css'
import profileImageStyles from '../../atoms/ProfileImage/ProfileImage.module.css'
import styles from './ArtistProfileList.module.css'

type Artists = ProfileImageProps & { linkAll?: boolean }

export interface ArtistProfileListProps {
  title?: string
  titleSize?: TextSizes
  description?: string
  artists: Artists[]
  countType?: 'works' | 'articles'
  totalArtists: number
  totalArtistsTitle?: string
  allBtnHandler?: () => void
  showNumberOfWorks?: boolean
  hideViewAll?: boolean
  testID?: string
  type?: ProfileImageProps['type']
  artistId?: string
  searchInsightsEvent?: TSearchAnalystEventProps
  recordType?: RecordTypes
  ariaControlId?: string
  ariaExpanded?: boolean
  showViewAll?: boolean
  viewAllLabel?: string
  theme?: ComponentTheme
  verticalPadding?: boolean
  noPaddingBottom?: boolean
  moduleId?: string
  alwaysShowViewAll?: boolean
  noPrefetch?: boolean
}

export default function ArtistProfileList({
  title,
  titleSize = TextSizes.primaryTitleLrg,
  description,
  artists,
  countType,
  totalArtists,
  totalArtistsTitle,
  showNumberOfWorks = false,
  hideViewAll,
  allBtnHandler,
  testID,
  type = ProfileImageType.Artist,
  artistId,
  searchInsightsEvent,
  recordType = RecordTypes.ARTIST,
  ariaControlId,
  ariaExpanded,
  showViewAll = false,
  viewAllLabel,
  theme,
  verticalPadding,
  noPaddingBottom,
  moduleId,
  alwaysShowViewAll = false,
  noPrefetch = false,
}: ArtistProfileListProps) {
  const t = useTranslations('common')
  const tArticle = useTranslations('articlePage')
  const newArtists = [...uniqueObjects(artists)].filter(Boolean)

  if (!hideViewAll) newArtists.splice(7, newArtists.length)
  const isMobile = useMediaQuery(`(max-width: 767px)`)

  const router = useRouter()

  const [ref, inView] = useInView({
    triggerOnce: true,
  })

  return (
    <Fixture
      theme={theme}
      verticalPadding={verticalPadding}
      noPaddingBottom={noPaddingBottom}
      id={moduleId}
    >
      <div
        className={classnames(styles.base, inView && styles.inview)}
        data-testid={testID}
        ref={ref}
        key={`aritst-profile-list-${title ? title : ''}-${router.asPath}`}
      >
        <Container gutters={gutterTypes.always}>
          <div>
            <div className={styles.titleWrapper}>
              {title && (
                <>
                  <AnimatedSectionTitle
                    className={styles.title}
                    size={titleSize}
                  >
                    {title}
                  </AnimatedSectionTitle>
                </>
              )}
              {showViewAll && (
                <div
                  className={classnames(
                    !isMobile && !alwaysShowViewAll && styles.hideViewAllMobile
                  )}
                >
                  <Button
                    className={classnames(styles.viewAllButton)}
                    variant={ButtonVariants.textLinkSmallUnderline}
                    button={{
                      type: ButtonTypes.button,
                      onClick: allBtnHandler,
                    }}
                  >
                    {viewAllLabel || t('viewAll')}
                  </Button>
                </div>
              )}
            </div>
            <div className={styles.keyLine}></div>
            {description && (
              <Text
                Tag="p"
                size={TextSizes.bodyMed}
                className={styles.description}
              >
                {description}
              </Text>
            )}
          </div>
        </Container>
        <div className={classnames(containerStyles.container, styles.list)}>
          {newArtists.map((artist, idx) => (
            <div className={styles.listItem} key={`${artist.name}-${idx}`}>
              <ProfileImage
                {...artist}
                countType={countType}
                showNumberOfWorks={true}
                testID={testID && `profile-${testID}`}
                type={type}
                artistId={artistId}
                searchInsightsEvent={searchInsightsEvent}
                recordType={recordType}
                fallBackToInitials
                noPrefetch
              />
            </div>
          ))}
          {totalArtists > newArtists.length && !hideViewAll && (
            <button
              onClick={async () => {
                allBtnHandler?.()
                multiPostHogEventPusher([
                  {
                    eventType: `View all ${recordType} clicked`,
                  },
                ])
              }}
              className={classnames(profileImageStyles.link, styles.link)}
              aria-controls={ariaControlId}
              aria-label={tArticle('openNavigation')}
              aria-expanded={ariaExpanded}
            >
              <div className={profileImageStyles.imageContainer}>
                <div
                  className={classnames(
                    profileImageStyles.image,
                    styles.counter
                  )}
                >
                  <Text
                    size={TextSizes.primaryTitleLrg}
                    className={styles.number}
                  >
                    {t('viewAll')}
                  </Text>
                </div>
              </div>
            </button>
          )}
        </div>
      </div>
    </Fixture>
  )
}
